/**
 * Delivery-outcome classification for e-mail events (mail.emaillogs, written by Micro.Mail
 * from SendGrid webhook events).
 *
 * Events fall into four classes:
 *  - terminal success:  delivered, open, click        → the mail arrived
 *  - terminal failure:  bounce, dropped, blocked, failed → the mail will not arrive
 *  - neutral:           processed                     → accepted for sending, says nothing about the outcome
 *  - transient:         deferred                      → temporary failure, SendGrid keeps retrying
 *
 * Whether a delivery failed is decided by the LATEST outcome-relevant event, ignoring the
 * neutral/transient ones. History of this logic: #9302 introduced the indicator (flagged any
 * non-success event → false positives on deferred→delivered), #20776 inverted the filter
 * (recipients without success events → "processed" masked real bounces, false negatives),
 * #22919 introduced this sequence-based decision.
 */

const TERMINAL_SUCCESS_EVENTS = ["delivered", "open", "click"];
const TERMINAL_FAILURE_EVENTS = ["bounce", "dropped", "blocked", "failed"];

export interface IOutcomeRelevantEmailLog {
    event: string | null;
}

/**
 * Returns the e-mail log describing the failed delivery, or undefined if the delivery did not
 * (or did not yet terminally) fail.
 *
 * Expects the logs ordered by timestamp DESCENDING (newest first), as they are queried in the
 * history fragments (`emaillogs(order_by: { timestamp: desc })`).
 */
export function getFailedDeliveryEmailLog<T extends IOutcomeRelevantEmailLog>(
    emaillogs: T[] | null | undefined
): T | undefined {
    const latestOutcomeRelevantLog = emaillogs?.find((log) => {
        const event = log.event ?? "";
        return TERMINAL_SUCCESS_EVENTS.includes(event) || TERMINAL_FAILURE_EVENTS.includes(event);
    });

    if (latestOutcomeRelevantLog && TERMINAL_FAILURE_EVENTS.includes(latestOutcomeRelevantLog.event ?? "")) {
        return latestOutcomeRelevantLog;
    }

    return undefined;
}
